import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import PortalSwitcher from "@/components/PortalSwitcher";
import CrashReadyVoice from "@/components/CrashReadyVoice";
import DriverProfileNudge from "@/components/DriverProfileNudge";
import { LayoutDashboard, FolderLock, ShieldCheck, User, LogOut, Menu, X, Scale, FileText, CalendarDays } from "lucide-react";
import { useState } from "react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/cases", label: "My Cases", icon: FileText },
  { to: "/evidence", label: "Evidence Vault", icon: FolderLock },
  { to: "/appointments", label: "Appointments", icon: CalendarDays },
  { to: "/profile", label: "Profile", icon: User },
];

const PRO_ROLES = ["attorney", "admin", "affiliate", "pilot", "investigator"];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const navItems = NAV;
  const isClient = !PRO_ROLES.some((r) => user?.roles?.includes(r)) && !user?.pilot_status && !user?.nurse_status;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="brand-logo">
            <div className="h-8 w-8 rounded-xl bg-navy flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" strokeWidth={2.2} />
            </div>
            <span className="font-heading font-extrabold text-lg tracking-tight text-navy">Crash Ready</span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {isClient && <DriverProfileNudge className="mr-1" />}
            {navItems.map(({ to, label, icon: Icon }) => {
              const active = location.pathname === to;
              return (
                <Link key={to} to={to} data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${active ? "bg-cobalt-light text-cobalt" : "text-slate-600 hover:bg-slate-100"}`}>
                  <Icon className="w-4 h-4" /> {label}
                </Link>
              );
            })}
            <button onClick={handleLogout} data-testid="logout-btn"
              className="ml-2 flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 transition-all">
              <LogOut className="w-4 h-4" /> Logout
            </button>
            <div className="ml-2"><PortalSwitcher /></div>
          </nav>

          <button className="md:hidden p-2 text-navy" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {open && (
          <div className="md:hidden border-t border-slate-100 bg-white px-4 py-3 space-y-1">
            {isClient && <div className="px-1 pb-2" onClick={() => setOpen(false)}><DriverProfileNudge /></div>}
            {navItems.map(({ to, label, icon: Icon }) => (
              <Link key={to} to={to} onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-50">
                <Icon className="w-5 h-5" /> {label}
              </Link>
            ))}
            <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50">
              <LogOut className="w-5 h-5" /> Logout
            </button>
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">{children}</main>
      <CrashReadyVoice />
    </div>
  );
}
